import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SlotPicker from '../shared/SlotPicker';
import DateCalendar from '../shared/DateCalendar';
import ContactForm from '../shared/ContactForm';
import ConfirmView from '../shared/ConfirmView';
import ProductCardList from '../shared/ProductCardList';
import StoreSelector from '../shared/StoreSelector';
import EmptyHint from '../shared/EmptyHint';
import { STEP_LABELS, buildVisibleSteps } from '../steps';
import type { AppointmentFormState } from '../useAppointmentForm';

interface Props {
  state: AppointmentFormState;
}

export default function CalcomLayout({ state }: Props) {
  const {
    config,
    services,
    consultants,
    slots,
    selectedServiceId,
    selectedProductId,
    selectedConsultantId,
    selectedSlot,
    selectedService,
    selectedProduct,
    selectedConsultant,
    productsForSelectedService,
    shouldShowProductStep,
    isProductRequired,
    selectedDate,
    setSelectedDate,
    selectServiceAndAdvance,
    selectProductAndAdvance,
    selectConsultant,
    selectSlot,
    submitBooking,
    step,
    goNext,
    goBack,
    customer,
    error,
    captchaError,
    loading,
    bookingResult,
  } = state;

  // Flusso a step focalizzati (una schermata per fase) al posto dello scroll
  // unico precedente: il carico cognitivo resta costante anche con molti
  // consulenti/tipologie. Auto-advance cancellabile mutuato dal DoctolibLayout:
  // il timer che avanza dopo una scelta (Data/Consulente/Orario) va annullato
  // se l'utente torna indietro entro la finestra (~200ms) o se smonta.
  const advanceTimer = useRef<number | null>(null);
  const cancelAdvance = () => {
    if (advanceTimer.current !== null) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  };
  const scheduleAdvance = () => {
    cancelAdvance();
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null;
      goNext();
    }, 200);
  };
  const handleBack = () => {
    cancelAdvance();
    goBack();
  };
  useEffect(() => cancelAdvance, []);

  // Task #515 + #525: prodotti collegati alla tipologia (lista inline sotto la
  // card della tipologia attiva). 'required' è l'unica modalità che obbliga la
  // scelta; 'always' mostra sempre il selettore ma resta opzionale; 'auto' lo
  // mostra solo con >1 prodotti. Allineato a useAppointmentForm.isProductRequired().
  const products = productsForSelectedService || [];
  const showProducts = shouldShowProductStep();
  const productRequired = isProductRequired();
  const productMissing = showProducts && productRequired && !selectedProductId;
  // Task #2830 — quando la scelta sede è attiva, la sede è prerequisito: la
  // tipologia resta bloccata finché il cliente non sceglie una sede.
  const storeGateActive = !!config?.allowStoreChoice && !state.selectedStoreId;

  // Apply branding via CSS variables
  useEffect(() => {
    if (!config) return;
    const el = document.querySelector('.aw-root') as HTMLElement | null;
    if (el) {
      el.style.setProperty('--aw-primary', config.branding.primaryColor || '#6366f1');
      el.style.setProperty('--aw-accent', config.branding.accentColor || '#ec4899');
      if (config.branding.fontFamily) {
        el.style.setProperty('--aw-font', config.branding.fontFamily);
      }
    }
  }, [config]);

  if (!config) {
    return (
      <div className="aw-root aw-shell aw-style-calcom" style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 className="aw-spin" size={32} />
      </div>
    );
  }

  const themeClass = config.branding.defaultTheme === 'dark' ? 'aw-dark' : '';

  if (bookingResult) {
    return (
      <div className={`aw-root aw-shell aw-style-calcom ${themeClass}`}>
        <ConfirmView state={state} />
      </div>
    );
  }

  const stepsList = buildVisibleSteps(config);
  const currentIdx = stepsList.indexOf(step);

  return (
    <div className={`aw-root aw-shell aw-style-calcom ${themeClass}`}>
      <header className="aw-header">
        {config.branding.logoUrl && <img src={config.branding.logoUrl} alt="" className="aw-logo" />}
        <div>
          <h2 className="aw-title">{config.name}</h2>
          {config.description && <p className="aw-subtitle">{config.description}</p>}
        </div>
      </header>

      {/* Navigazione indietro + indicatore di avanzamento (flusso a step).
          Il back compare su ogni step tranne il primo ('service'); sullo step
          'contact' il back vive nella barra azioni in fondo al form. Estetica
          cal.com minimale: solo freccia + testo, niente stepper numerato. */}
      {currentIdx >= 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 14px' }}>
          {step !== 'service' && step !== 'contact' && (
            <button type="button" className="aw-btn aw-btn-ghost" onClick={handleBack} style={{ padding: '4px 10px' }}>
              ← Indietro
            </button>
          )}
          <span style={{ fontSize: 12, color: 'var(--aw-muted)', fontWeight: 500 }}>
            Step {currentIdx + 1} di {stepsList.length} · {STEP_LABELS[step]}
          </span>
        </div>
      )}

      {error && !captchaError && <div className="aw-error" role="alert">{error}</div>}

      <div className="aw-flow">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {step === 'service' && (
              <section className="aw-flow-section">
                {/* Sede (prerequisito): sullo step iniziale. */}
                <StoreSelector state={state} />
                <h3 className="aw-section-title">Tipologia di appuntamento</h3>
                {storeGateActive ? (
                  <div className="aw-hint" role="status">
                    Seleziona prima una sede per vedere le tipologie di appuntamento disponibili.
                  </div>
                ) : (
                  <>
                  <div className="aw-grid aw-grid-1">
                    {services.map((s) => {
                      const isSelected = selectedServiceId === s.id;
                      // Task #525: dropdown inline sotto la card della tipologia selezionata.
                      const inlineProducts = isSelected ? products : [];
                      const showInlineDropdown = isSelected && showProducts && inlineProducts.length > 0;
                      return (
                        <div key={s.id}>
                          <button
                            type="button"
                            className={`aw-card ${isSelected ? 'aw-selected' : ''}`}
                            onClick={() => selectServiceAndAdvance(s.id)}
                            style={showInlineDropdown ? { marginBottom: 8 } : undefined}
                          >
                            <div className="aw-card-row">
                              <span style={{ width: 8, height: 8, borderRadius: 999, background: s.color || 'var(--aw-primary)' }} />
                              <h4>{s.name}</h4>
                            </div>
                            <p>{s.durationMinutes} min{s.description ? ` · ${s.description}` : ''}</p>
                          </button>
                          {showInlineDropdown && (
                            <ProductCardList
                              products={inlineProducts}
                              selectedProductId={selectedProductId}
                              onSelect={selectProductAndAdvance}
                              required={productRequired}
                              missing={productMissing}
                              showPrice={config.showPrice === true}
                            />
                          )}
                        </div>
                      );
                    })}
                    {!services.length && (
                      <p style={{ color: 'var(--aw-muted)', fontSize: 13 }}>Nessuna tipologia disponibile</p>
                    )}
                  </div>
                  {/* Modalità prodotto opzionale ('always' / 'auto' con >1): il
                      picker resta mostrato ma non obbliga; questo "Continua"
                      consente l'avanzamento senza scegliere un prodotto. In
                      'required' il bottone non compare (scelta obbligatoria,
                      avanzamento solo via selectProductAndAdvance). */}
                  {selectedServiceId && showProducts && !productRequired && (
                    <div className="aw-actions">
                      <button type="button" className="aw-btn aw-btn-primary" onClick={() => { cancelAdvance(); goNext(); }}>
                        Continua
                      </button>
                    </div>
                  )}
                  </>
                )}
              </section>
            )}

            {step === 'date' && (
              <section className="aw-flow-section">
                {selectedService && (
                  <div className="aw-summary">
                    <div><span>Servizio</span><strong>{selectedService.name}</strong></div>
                    {selectedProduct && <div><span>Prodotto</span><strong>{selectedProduct.name}</strong></div>}
                  </div>
                )}
                <h3 className="aw-section-title">Data</h3>
                <DateCalendar
                  selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); scheduleAdvance(); }}
                  maxAdvanceDays={config.rules.maxAdvanceDays ?? 90}
                />
              </section>
            )}

            {step === 'consultant' && config.consultantFilterMode !== 'any' && (
              <section className="aw-flow-section">
                <h3 className="aw-section-title">Consulente</h3>
                <div className="aw-grid aw-grid-1">
                  {consultants.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`aw-card ${selectedConsultantId === c.id ? 'aw-selected' : ''}`}
                      onClick={() => { selectConsultant(c.id); scheduleAdvance(); }}
                    >
                      <div className="aw-consultant-card">
                        {c.sitePhotoUrl ? (
                          <img src={c.sitePhotoUrl} alt={c.fullName} className="aw-avatar" />
                        ) : (
                          <div className="aw-avatar">{c.firstName?.[0]}{c.lastName?.[0]}</div>
                        )}
                        <div>
                          <h4>{c.fullName}</h4>
                          {c.bio && <p>{c.bio.slice(0, 80)}{c.bio.length > 80 ? '...' : ''}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                  {!consultants.length && !loading.consultants && (
                    <EmptyHint variant="consultants" />
                  )}
                </div>
              </section>
            )}

            {step === 'slot' && (
              <section className="aw-flow-section">
                <h3 className="aw-section-title">Orari disponibili</h3>
                {(selectedConsultant || selectedService) && (
                  <div className="aw-summary">
                    {selectedConsultant && <div><span>Consulente</span><strong>{selectedConsultant.fullName}</strong></div>}
                    {selectedService && <div><span>Servizio</span><strong>{selectedService.name}</strong></div>}
                  </div>
                )}
                <SlotPicker
                  slots={slots}
                  selected={selectedSlot}
                  onSelect={(s) => { selectSlot(s); scheduleAdvance(); }}
                  loading={loading.slots}
                />
              </section>
            )}

            {step === 'contact' && selectedSlot && selectedService && (
              <section className="aw-flow-section">
                <div className="aw-summary">
                  <div><span>Servizio</span><strong>{selectedService.name}</strong></div>
                  {selectedProduct && <div><span>Prodotto</span><strong>{selectedProduct.name}</strong></div>}
                  {selectedConsultant && <div><span>Consulente</span><strong>{selectedConsultant.fullName}</strong></div>}
                  <div>
                    <span>Data e ora</span>
                    <strong>
                      {new Date(selectedSlot.start).toLocaleString('it-IT', {
                        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </strong>
                  </div>
                  <div>
                    <span>Durata</span>
                    <strong>
                      {(Math.round((new Date(selectedSlot.end).getTime() - new Date(selectedSlot.start).getTime()) / 60000) || selectedService.durationMinutes)} min
                    </strong>
                  </div>
                </div>
                <ContactForm state={state} />
                <div className="aw-actions aw-actions-sticky">
                  <button type="button" className="aw-btn aw-btn-ghost" onClick={handleBack}>
                    Indietro
                  </button>
                  <button
                    type="button"
                    className="aw-btn aw-btn-primary"
                    disabled={
                      loading.booking ||
                      !customer.name ||
                      !customer.email ||
                      !customer.phone ||
                      !customer.privacyAccepted ||
                      (config.allowStoreChoice && !state.selectedStoreId) ||
                      (config.gdpr?.consents || []).some(
                        (c) => c.required
                          && c.key !== 'privacy_policy' && c.key !== 'privacy' && c.key !== 'privacy_acceptance' && c.key !== 'marketing'
                          && !customer.gdprConsents?.[c.key],
                      ) ||
                      ((!!config.antiAbuse.hcaptchaEnabled || !!config.antiAbuse.recaptchaEnabled) && !state.captchaToken)
                    }
                    onClick={() => submitBooking()}
                  >
                    {loading.booking ? <Loader2 className="aw-spin" size={16} /> : 'Conferma prenotazione'}
                  </button>
                </div>
              </section>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
